import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AuthContext } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const AUTH_URL = 'https://auth.emergentagent.com';

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

const fieldClass =
  'w-full bg-[#0c0f17]/60 border border-[#262A36] rounded-lg px-4 py-3 text-sm text-white placeholder-[#4b5563] focus:border-[#1E8F89] focus:outline-none focus:ring-1 focus:ring-[#1E8F89]/40 transition-colors';

export const AuthDialog = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const { setUser } = useContext(AuthContext);
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = mode === 'login' ? 'login' : 'register';
      const payload = mode === 'login' ? { email, password } : { email, password, name };
      const { data } = await axios.post(`${API}/auth/${endpoint}`, payload, { withCredentials: true });
      setUser(data.user);
      toast.success(mode === 'login' ? 'Access granted' : 'Researcher registered');
      onOpenChange(false);
      navigate('/dashboard');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password`, { email }, { withCredentials: true });
      setForgotSent(true);
      toast.success(data.message || 'Reset transmission dispatched');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Unable to issue reset token');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    const redirectUrl = `${window.location.origin}/`;
    window.location.assign(`${AUTH_URL}/?redirect=${encodeURIComponent(redirectUrl)}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md glass-hud border-[#262A36] rounded-xl p-0 text-white scanlines"
        data-testid="auth-dialog"
      >
        <div className="p-8 relative z-10">
          <DialogHeader className="mb-6">
            <p className="mono-label mb-2">
              {mode === 'forgot' ? 'Archive Access Token' : mode === 'login' ? 'Secure Access' : 'Researcher Registration'}
            </p>
            <DialogTitle className="font-serif-display text-3xl font-light tracking-tight text-white">
              {mode === 'forgot' ? 'Reset Transmission' : mode === 'login' ? 'Enter Research Lab' : 'Request Access'}
            </DialogTitle>
          </DialogHeader>

          {mode === 'forgot' ? (
            forgotSent ? (
              <div className="space-y-5" data-testid="forgot-sent-confirmation">
                <p className="text-sm text-[#949BAA] leading-relaxed font-mono-tech">
                  If that address exists in the archive, a reset transmission is on its way. The access token expires in 30 minutes.
                </p>
                <button
                  type="button"
                  onClick={() => { setMode('login'); setForgotSent(false); }}
                  className="btn-ambient w-full"
                  data-testid="forgot-back-to-login"
                >
                  Return to access
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgot} className="space-y-4" data-testid="forgot-form">
                <p className="text-sm text-[#6B7280] leading-relaxed mb-2 font-mono-tech">
                  Enter the address tied to your archive account. We will dispatch a single-use reset token.
                </p>
                <div>
                  <label className="mono-meta mb-2 block">Email</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className={fieldClass} data-testid="forgot-email-input" />
                </div>
                <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="forgot-submit-button">
                  {loading ? 'Dispatching…' : 'Send reset transmission'}
                </button>
                <button
                  type="button"
                  onClick={() => setMode('login')}
                  className="w-full text-center font-mono-tech text-[0.62rem] uppercase tracking-[0.2em] text-[#6B7280] hover:text-[#26B0A8] transition-colors pt-1"
                  data-testid="forgot-cancel"
                >
                  ← Back to sign in
                </button>
              </form>
            )
          ) : (
          <>
          <div className="flex border border-[#262A36] rounded-lg p-1 mb-6 bg-[#0c0f17]/50">
            <button
              type="button"
              onClick={() => setMode('login')}
              className={`flex-1 py-2 font-mono-tech text-[0.62rem] uppercase tracking-[0.2em] rounded-md transition-all ${mode === 'login' ? 'bg-[#1E8F89]/15 text-[#26B0A8]' : 'text-[#949BAA]'}`}
              data-testid="auth-tab-login"
            >
              Sign In
            </button>
            <button
              type="button"
              onClick={() => setMode('register')}
              className={`flex-1 py-2 font-mono-tech text-[0.62rem] uppercase tracking-[0.2em] rounded-md transition-all ${mode === 'register' ? 'bg-[#1E8F89]/15 text-[#26B0A8]' : 'text-[#949BAA]'}`}
              data-testid="auth-tab-register"
            >
              Register
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <div>
                <label className="mono-meta mb-2 block">Name</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} required className={fieldClass} data-testid="auth-name-input" />
              </div>
            )}
            <div>
              <label className="mono-meta mb-2 block">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className={fieldClass} data-testid="auth-email-input" />
            </div>
            <div>
              <label className="mono-meta mb-2 block">Passphrase</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className={fieldClass} data-testid="auth-password-input" />
            </div>
            {mode === 'login' && (
              <button
                type="button"
                onClick={() => { setForgotSent(false); setMode('forgot'); }}
                className="block w-full text-right font-mono-tech text-[0.62rem] uppercase tracking-[0.18em] text-[#6B7280] hover:text-[#26B0A8] transition-colors"
                data-testid="forgot-password-trigger"
              >
                Reset passphrase
              </button>
            )}
            <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="auth-submit-button">
              {loading ? 'Please wait…' : mode === 'login' ? 'Enter Research Lab' : 'Request Access'}
            </button>
          </form>

          <div className="flex items-center gap-4 my-5">
            <div className="flex-1 h-px bg-[#262A36]" />
            <span className="mono-meta">or</span>
            <div className="flex-1 h-px bg-[#262A36]" />
          </div>

          <button type="button" onClick={handleGoogle} className="btn-ambient w-full" data-testid="auth-google-button">
            Continue with Google
          </button>
          </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AuthDialog;
